import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { useChatbots } from '@/lib/sdk/chatbot/queries';
import { useMasterDataDatasets, useMasterDataList } from '@/lib/sdk/master-data/queries';
import { useFieldDefinitions } from '@/lib/sdk/master-data-field-definitions/queries';
import { useCreateQrJob, useQrJob, useQrJobItems } from '@/lib/sdk/qr-jobs/queries';
import { QrJobApi } from '@/lib/sdk/qr-jobs/api';

export const PAGE_SIZE = 20;
export const RECORD_PAGE_SIZE = 50;

const bulkQrSchema = z.object({
  chatbot_id: z.string().min(1, 'Select a chatbot'),
  dataset: z.string().min(1, 'Select a dataset'),
  attribute_sources: z.array(z.string()).min(1, 'Pick at least one attribute'),
  scope: z.enum(['all', 'selected']),
  keys: z.array(z.string()),
}).refine((values) => values.scope === 'all' || values.keys.length > 0, {
  message: 'Select at least one record',
  path: ['keys'],
});

export type BulkQrFormData = z.infer<typeof bulkQrSchema>;

export interface AttributeOption {
  value: string;
  label: string;
}

export function useBulkQrJob() {
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id ?? '';
  const [searchParams, setSearchParams] = useSearchParams();
  const jobId = searchParams.get('job') ?? '';
  const [page, setPage] = useState(1);

  const form = useForm<BulkQrFormData>({
    resolver: zodResolver(bulkQrSchema),
    defaultValues: { chatbot_id: '', dataset: '', attribute_sources: [], scope: 'all', keys: [] },
  });
  const [chatbotId, dataset, attributeSources, scope, selectedKeys] = useWatch({
    control: form.control,
    name: ['chatbot_id', 'dataset', 'attribute_sources', 'scope', 'keys'],
  });

  const { data: chatbotsData } = useChatbots({ orgId, limit: 100, active_only: true });
  const { data: datasets } = useMasterDataDatasets(orgId);
  const { data: fieldDefinitions } = useFieldDefinitions(orgId, dataset);

  const chatbotOptions = useMemo(
    () => (chatbotsData?.items ?? []).map((chatbot) => ({ value: chatbot.id, label: `${chatbot.title} (${chatbot.short_code})` })),
    [chatbotsData],
  );
  const datasetOptions = useMemo(
    () => (datasets ?? []).map((name) => ({ value: name, label: name })),
    [datasets],
  );
  const attributeOptions = useMemo<AttributeOption[]>(
    () => [
      { value: 'key', label: 'Key' },
      { value: 'label', label: 'Label' },
      ...(fieldDefinitions ?? []).map((definition) => ({ value: `fields.${definition.field_name}`, label: definition.label })),
    ],
    [fieldDefinitions],
  );
  const labelFor = (source: string): string =>
    attributeOptions.find((option) => option.value === source)?.label ?? source;

  const selectedCode = chatbotsData?.items.find((chatbot) => chatbot.id === chatbotId)?.short_code ?? '<CODE>';
  const messagePreview = [
    `/start ${selectedCode}`,
    ...attributeSources.map((source, index) => `${index + 1}:<${labelFor(source)}>`),
  ].join(' ');

  const setSources = (sources: string[]) =>
    form.setValue('attribute_sources', sources, { shouldValidate: form.formState.isSubmitted });
  const addSource = (source: string) => {
    if (source && !attributeSources.includes(source)) setSources([...attributeSources, source]);
  };
  const removeSource = (source: string) => setSources(attributeSources.filter((existing) => existing !== source));
  const setKeys = (keys: string[]) =>
    form.setValue('keys', keys, { shouldValidate: form.formState.isSubmitted });
  const onDatasetChange = () => {
    setSources([]);
    setKeys([]);
    setRecordPage(1);
  };

  // Record picker for scope "selected": the dataset's rows, filterable by text and by creation
  // date so newly added records are easy to pick out. Selection is kept across pages.
  const [recordSearch, setRecordSearch] = useState('');
  const [recordsAddedSince, setRecordsAddedSince] = useState('');
  const [recordPage, setRecordPage] = useState(1);
  const { data: recordsData, isLoading: isLoadingRecords } = useMasterDataList(orgId, {
    dataset,
    page: recordPage,
    page_size: RECORD_PAGE_SIZE,
    ...(recordSearch ? { search: recordSearch } : {}),
    ...(recordsAddedSince ? { from: new Date(`${recordsAddedSince}T00:00:00`).toISOString() } : {}),
  }, { enabled: scope === 'selected' && !!dataset });
  const recordTotal = recordsData?.total ?? 0;
  const onRecordSearch = (value: string) => { setRecordSearch(value); setRecordPage(1); };
  const onRecordsAddedSince = (value: string) => { setRecordsAddedSince(value); setRecordPage(1); };
  const toggleKey = (key: string, checked: boolean) =>
    setKeys(checked ? [...selectedKeys.filter((existing) => existing !== key), key] : selectedKeys.filter((existing) => existing !== key));
  const togglePageKeys = (keys: string[], checked: boolean) =>
    setKeys(checked
      ? [...selectedKeys, ...keys.filter((key) => !selectedKeys.includes(key))]
      : selectedKeys.filter((key) => !keys.includes(key)));

  const { data: job } = useQrJob(orgId, jobId);
  const isRunning = job?.status === 'running';
  const isCompleted = job?.status === 'completed';
  const processed = (job?.succeeded ?? 0) + (job?.failed ?? 0);
  const { data: itemsData, isLoading: isLoadingItems } = useQrJobItems(orgId, jobId, { page, page_size: PAGE_SIZE }, processed);

  const createJob = useCreateQrJob(orgId);
  const onGenerate = form.handleSubmit((values) => {
    const { scope: selectedScope, keys, ...request } = values;
    createJob.mutate(selectedScope === 'selected' ? { ...request, keys } : request, {
      onSuccess: (created) => {
        setPage(1);
        setSearchParams({ job: created.id });
      },
    });
  });

  const total = itemsData?.total ?? 0;

  return {
    form,
    chatbotOptions, datasetOptions, attributeOptions,
    dataset, attributeSources, addSource, removeSource, labelFor, onDatasetChange,
    messagePreview,
    scope, selectedKeys, setKeys, toggleKey, togglePageKeys,
    records: recordsData?.items ?? [], isLoadingRecords, recordTotal,
    recordSearch, onRecordSearch, recordsAddedSince, onRecordsAddedSince,
    recordPage, setRecordPage, recordPageCount: Math.max(1, Math.ceil(recordTotal / RECORD_PAGE_SIZE)),
    onGenerate, isSubmitting: createJob.isPending, createError: createJob.error,
    job, isRunning, isCompleted, processed,
    items: itemsData?.items ?? [], isLoadingItems,
    page, setPage, pageCount: Math.max(1, Math.ceil(total / PAGE_SIZE)),
    downloadUrl: jobId ? QrJobApi.downloadUrl(orgId, jobId) : '',
  };
}
